import moment from "moment";
import { useHistory } from "react-router-dom";

import {
  AvailabilityInfo,
  BasicBooking,
  Booking,
  BookingAvailabilities,
  BookingOffer,
  BookingPricings,
  BookingRoom,
} from "../definitions/booking";
import { GeneralObject } from "../definitions/general";
import {
  Property,
  PropertyRoom,
  RoomAvailability,
} from "../definitions/property";
import { formatDateForServer } from "../services/booking/utils";
import {
  useAnonymousApi,
  useAuthenticatedApi,
  useUnauthenticatedApi,
} from "./useApi";

type Obj = { [key: string]: any };

const useBookingService = (endpoint = "stay") => {
  const api = useAnonymousApi("STAY_AVAILABILITY");
  const couponApi = useAnonymousApi("STAY_BOOKINGS_APPLY_COUPON");
  const offeredPricing = useAnonymousApi("STAY_OFFERED_PRICING");
  const hostelDataApi = useUnauthenticatedApi("STAY_OPERATORS");

  const crsApi = useAuthenticatedApi("CRS_BOOKING");

  const history = useHistory();

  const getAvailability = async (
    bookingDetails: BasicBooking,
    _offeredPrice?: boolean
  ) => {
    const checkin = formatDateForServer(bookingDetails.checkInDate);
    const checkout = formatDateForServer(bookingDetails.checkOutDate);
    if (endpoint === "crs") {
      if (crsApi) {
        return await crsApi.get(
          `availability/?checkin=${checkin}&checkout=${checkout}&property_code=${bookingDetails.propertyCode}`
        );
      }
    } else if (_offeredPrice && offeredPricing) {
      return await offeredPricing.get(
        `/?checkin=${checkin}&checkout=${checkout}&property_code=${bookingDetails.propertyCode}`
      );
    } else {
      if (api) {
        return await api.get(
          `/?checkin=${checkin}&checkout=${checkout}&property_code=${bookingDetails.propertyCode}`
        );
      }
    }
  };

  const applyCoupon = async (bookingDetails: Booking) => {
    if (endpoint === "crs" && crsApi) {
      return await crsApi.post(
        "stay/apply_coupon/",
        JSON.stringify(bookingDetails)
      );
    } else if (couponApi) {
      return await couponApi.post("", JSON.stringify(bookingDetails));
    }
  };

  const createNewBooking = (bookingDetails: BasicBooking) => {
    history.push(
      `/property/${bookingDetails.propertyCode}/?checkin=${formatDateForServer(
        bookingDetails.checkInDate
      )}&checkout=${formatDateForServer(bookingDetails.checkOutDate)}`
    );
  };

  const decodeBookingSearchParams: (
    searchString: string
  ) => BasicBooking | null = (searchString: string) => {
    const searchParams = new URLSearchParams(searchString);
    const propertyCode = searchParams.get("propertyCode");
    const checkInDate = searchParams.get("checkin");
    const checkOutDate = searchParams.get("checkout");
    if (propertyCode && checkInDate && checkOutDate) {
      const bookingDetails: BasicBooking = {
        propertyCode,
        checkInDate: moment(checkInDate),
        checkOutDate: moment(checkOutDate),
      };
      return bookingDetails;
    } else {
      return null;
    }
  };

  const decodeDatesFromSearchParams: (searchString: string) => {
    checkInDate: moment.Moment | null;
    checkOutDate: moment.Moment | null;
  } = (searchString: string) => {
    const searchParams = new URLSearchParams(searchString);
    const checkInDate = searchParams.get("checkin");
    const checkOutDate = searchParams.get("checkout");
    if (checkInDate && checkOutDate) {
      return {
        checkInDate: moment(checkInDate),
        checkOutDate: moment(checkOutDate),
      };
    }
    return {
      checkInDate: null,
      checkOutDate: null,
    };
  };

  const getHostelData = async (propertySlug: string) => {
    try {
      const response = await hostelDataApi.get(`/${propertySlug}/`);
      console.log(response);
    } catch (error) { }
  };

  const getRoomAvailabilityAndMaxUnits: (
    availability: BookingAvailabilities | undefined,
    roomId: number
  ) => RoomAvailability = (availability, roomId) => {
    if (availability) {
      const roomAvailability: BookingAvailabilities | null =
        availability.filter((a) => +a.room_id === roomId);
      if (roomAvailability.length) {
        const hasAvailability = roomAvailability.every(
          (rA) => rA.bookable === true
        );
        const minUnit: number = Math.min(
          ...roomAvailability.map((r) => r.units)
        );
        return {
          isAvailable: minUnit > 0 ? true : false,
          maxUnits: minUnit,
          isBookable: hasAvailability,
        };
      }
    }
    return {
      isAvailable: false,
      maxUnits: 0,
      isBookable: false,
    };
  };

  const getRoomPrice: (
    pricing: BookingPricings | undefined,
    roomId: number
  ) => { price: number; nights: number } = (pricing, roomId) => {
    if (pricing) {
      const roomPricings: BookingPricings | null = pricing.filter(
        (a) => +a.room_id === roomId
      );
      if (roomPricings.length) {
        const cumulativePricing = roomPricings.reduce(
          (sum, rP) => rP.price + sum,
          0
        );
        return { price: cumulativePricing, nights: roomPricings.length };
      }
    }
    return { price: 0, nights: 0 };
  };

  const getOfferedRoomPrice: (
    pricing: BookingPricings | undefined,
    offer: BookingOffer,
    roomId: number,
    addonPrice: number
  ) => { price: number; discount: number; nights: number } = (
    pricing,
    offer,
    roomId,
    addonPrice
  ) => {
      if (pricing) {
        const roomPricings: BookingPricings | null = pricing.filter(
          (a) => +a.room_id === roomId
        );
        if (roomPricings.length) {
          let sum = 0;
          let discount = 0;
          // let discountNights = 0;
          roomPricings.forEach((roomPricing) => {
            const dayPrice = roomPricing.price + addonPrice;
            sum += dayPrice;
            const currentDate = moment(roomPricing.date).startOf("day");
            const maxBookingDate = moment(offer.max_booking_datetime).startOf(
              "day"
            );
            const minBookingDate = moment(offer.min_booking_datetime).startOf(
              "day"
            );
            const blackout_dates =
              offer.blackout_dates?.map((d) => moment(d).format("YYYY-MM-DD")) ||
              [];

            const weekDay = currentDate.get("isoWeekday") - 1;
            if (
              !blackout_dates.includes(
                moment(roomPricing?.date).format("YYYY-MM-DD")
              ) &&
              !roomPricing.slab_active &&
              ((offer.max_booking_datetime != null &&
                currentDate.isSameOrBefore(maxBookingDate)) ||
                offer.max_booking_datetime == null) &&
              ((offer.min_booking_datetime != null &&
                currentDate.isSameOrAfter(minBookingDate)) ||
                offer.max_booking_datetime == null) &&
              ((offer.max_booked_on_delta != null &&
                currentDate <= moment().add(offer.max_booked_on_delta, "days")) ||
                offer.max_booked_on_delta == null) &&
              ((offer.booking_weekdays != null &&
                offer.booking_weekdays.length > 0 &&
                offer.booking_weekdays.indexOf(weekDay) !== -1) ||
                offer.booking_weekdays == null ||
                offer.booking_weekdays.length === 0)
            ) {
              const currentDiscount =
                offer.discount_type === 1
                  ? (offer.discount_value / 100) * dayPrice
                  : offer.discount_type === 2
                    ? offer.discount_value
                    : offer.discount_type === 3
                      ? dayPrice - offer.discount_value
                      : 0;
              discount += Math.min(currentDiscount, dayPrice);

              // discountNights++;
              console.log(
                "Discount applied on:",
                roomPricing.date,
                "for",
                roomId,
                "discount",
                currentDiscount,
                dayPrice,
                offer.discount_value
              );
            }
          });
          console.log(
            "Final values for",
            roomId,
            sum,
            discount,
            roomPricings.length
          );
          return {
            price: sum,
            discount:
              offer.max_discount_value != null
                ? Math.min(discount, offer.max_discount_value)
                : discount,
            nights: roomPricings.length,
          };
        }
      }
      return { price: 0, discount: 0, nights: 0 };
    };

  const createRoomsFromAvailability: (
    rooms: PropertyRoom[],
    availabilityInfo: AvailabilityInfo
  ) => PropertyRoom[] = (rooms, availabilityInfo) => {
    const _rooms = rooms.map((room) => {
      const basePrice = getRoomPrice(availabilityInfo.pricing, room.id);
      const addonPrice =
        room._ref.split("-").length > 1
          ? room.addons.find((a) => a.id === +room._ref.split("-")[1])?.price ||
          0
          : 0;
      const roomPrice = basePrice.price / basePrice.nights + addonPrice;
      room.price = roomPrice;
      room.slab_active = availabilityInfo.pricing
        .filter((a) => a.room_id === room.id)
        .some((p) => p.slab_active === true);
      room.taxes = getRoomTaxes(availabilityInfo.pricing, room.id);
      room.availability = getRoomAvailabilityAndMaxUnits(
        availabilityInfo.availability,
        room.id
      );
      return { ...room };
    });
    return _rooms;
  };

  const createOfferedRoomsFromAvailability: (
    rooms: PropertyRoom[],
    availabilityInfo: AvailabilityInfo
  ) => PropertyRoom[] = (rooms, availabilityInfo) => {
    let baseRooms = rooms.map((room) => {
      const basePrice = getRoomPrice(availabilityInfo.pricing, room.id);
      room.price = basePrice.price;
      room.slab_active = availabilityInfo.pricing
        .filter((a) => a.room_id === room.id)
        .some((p) => p.slab_active === true);
      room.taxes = getRoomTaxes(availabilityInfo.pricing, room.id);
      room.availability = getRoomAvailabilityAndMaxUnits(
        availabilityInfo.availability,
        room.id
      );
      room.nights = basePrice.nights;
      return { ...room, _ref: `${room.id}` };
    });
    const baseRoomsToRemove = new Set();
    const offerRooms: PropertyRoom[] = [];
    availabilityInfo.offers.forEach((o) => {
      const set = new Set<string>();
      availabilityInfo.pricing.forEach((p) => {
        set.add(p.date);
      });
      const isApplicable = Array.from(set).some(
        (p) =>
          o.booking_weekdays.indexOf(moment(p).get("isoWeekday") - 1) !== -1
      );
      if (isApplicable) {
        o.inventories.forEach((inventoryId) => {
          const roomData = baseRooms.find(
            (room) => String(room.id) === String(inventoryId)
          );
          if (roomData) {
            const roomPricings: BookingPricings | null =
              availabilityInfo.pricing.filter(
                (a) => +a.room_id === roomData.id
              );
            const roomNights = roomPricings.length;
            if (roomData.addons && roomData.addons.length > 0) {
              const availableAddons = roomData.addons.map((a) => a.id);
              const addonsExist = o.addons.every(
                (a) => availableAddons.indexOf(a) !== -1
              );
              const addons = o.addons.map((a) =>
                roomData.addons.find((_a) => _a.id === a)
              );
              // const addonPrice = addons
              //   .map((a) => (a ? (a.price ? a.price : 0) : 0))
              //   .reduce((a: number, b: number) => a + b);
              let addonPrice = 0;
              addons.forEach((a) => {
                if (a) {
                  if (a.sell_datewise) {
                    addonPrice += a.price;
                  } else {
                    if (roomNights > 0) {
                      addonPrice += a.price / roomNights;
                    }
                  }
                }
              });
              if (addonsExist) {
                const offerAmount = getOfferedRoomPrice(
                  availabilityInfo.pricing,
                  o,
                  roomData.id,
                  addonPrice
                );
                const _offerRoom = {
                  ...roomData,
                  _ref: `${roomData.id}-${o.addons.join("-")}*${o.id}`,
                  name: o.is_suffix_text
                    ? `${roomData.name} ${o.inventory_name}`
                    : o.inventory_name,
                  description: o.is_suffix_text
                    ? `${roomData.description}\n${o.inventory_description}`
                    : o.inventory_description,
                  price: offerAmount.price,
                  discount: offerAmount.discount,
                  nights: offerAmount.nights || roomNights,
                  offerData: o,
                };
                const o_room = offerRooms.find((r) => r.id === _offerRoom.id);
                if (!o_room) {
                  offerRooms.push(_offerRoom);
                }
                if (
                  o_room &&
                  o_room.discount &&
                  _offerRoom.discount &&
                  o_room.discount < _offerRoom.discount
                ) {
                  offerRooms.splice(offerRooms.indexOf(o_room), 1);
                  offerRooms.push(_offerRoom);
                }
                if (!o.sell_base_inventory) {
                  baseRoomsToRemove.add(String(inventoryId));
                }
              }
            } else {
              const offerAmount = getOfferedRoomPrice(
                availabilityInfo.pricing,
                o,
                roomData.id,
                0
              );
              const _offerRoom = {
                ...roomData,
                _ref: `${roomData.id}-${o.addons.join("-")}*${o.id}`,
                name: o.is_suffix_text
                  ? `${roomData.name} ${o.inventory_name}`
                  : o.inventory_name,
                description: o.is_suffix_text
                  ? `${roomData.description}\n${o.inventory_description}`
                  : o.inventory_description,
                price: offerAmount.price,
                discount: offerAmount.discount,
                nights: offerAmount.nights || roomNights,
                offerData: o,
              };
              const o_room = offerRooms.find((r) => r.id === _offerRoom.id);
              if (!o_room) {
                offerRooms.push(_offerRoom);
              }
              if (
                o_room &&
                o_room.discount &&
                _offerRoom.discount &&
                o_room.discount < _offerRoom.discount
              ) {
                offerRooms.splice(offerRooms.indexOf(o_room), 1);
                offerRooms.push(_offerRoom);
              }
              if (!o.sell_base_inventory) {
                baseRoomsToRemove.add(String(inventoryId));
              }
            }
          }
        });
      }
    });
    baseRooms = baseRooms.filter((r) => !baseRoomsToRemove.has(String(r.id)));
    return [...baseRooms, ...offerRooms];
  };

  const getRoomTaxes: (
    pricing: BookingPricings | undefined,
    roomId: number
  ) => { cgst: number | null; sgst: number | null } = (pricing, roomId) => {
    if (pricing) {
      const roomPricings: BookingPricings | null = pricing.filter(
        (a) => +a.room_id === roomId
      );
      if (roomPricings.length) {
        const cumulativeCGST = roomPricings.reduce(
          (sum, rP) => rP.tax_breakup.cgst + sum,
          0
        );
        const cumulativeSGST = roomPricings.reduce(
          (sum, rP) => rP.tax_breakup.sgst + sum,
          0
        );
        return { cgst: cumulativeCGST, sgst: cumulativeSGST };
      }
    }
    return { cgst: null, sgst: null };
  };

  const buildAddonRooms: (data: Property) => Property = (data) => {
    const operatorData: Property = data;
    const rooms: PropertyRoom[] = [];
    operatorData.rooms.forEach((room) => {
      room._ref = `${room.id}`;
      rooms.push(room);
      if (room.addons?.length) {
        room.addons.forEach((a) => {
          const _newRoom = {
            ...room,
            name: `${room.name} with ${a.name}`,
            _ref: `${room.id}-${a.id}`,
            description: `${room.description}${a.description && a.description.length
              ? `\n\n${a.description}`
              : ""
              }`,
          };
          rooms.push(_newRoom);
        });
      }
    });
    operatorData.rooms = [...rooms];
    return operatorData;
  };

  const sortPropertyImages: (data: Property) => Property = (data) => {
    const operatorData: Property = data;
    operatorData.images = operatorData.images.sort((a, b) =>
      a.priority != null && b.priority != null ? b.priority - a.priority : 0
    );
    return operatorData;
  };

  const encodeRooms: (rooms: Array<BookingRoom>) => string = (
    rooms: Array<BookingRoom>
  ) => {
    // ROOMREF@COUNT_ROOMREF@COUNT
    return rooms.map((room) => room._ref + "@" + room.count).join("_");
  };

  const decodeRooms: (roomsString: string) => Array<BookingRoom> | null = (
    roomsString: string
  ) => {
    const rooms = roomsString.split("_");
    if (rooms.length > 0 && rooms.every((room: string) => room.includes("@"))) {
      const roomsData: BookingRoom[] = rooms.map((room: string) => {
        const roomSplit = room.split("@");
        const roomRef = roomSplit[0];
        const roomOfferSplit = roomRef.split("*");
        const defaultRoomId = roomRef.split("*")[0];
        const roomId = +defaultRoomId.split("-")[0];
        const addonId = defaultRoomId.split("-")[1]
          ? +defaultRoomId.split("-")[1]
          : null;
        const offerId = roomOfferSplit[1] ? +roomOfferSplit[1] : null;
        if (addonId) {
          const data: BookingRoom = {
            id: roomId,
            _ref: roomRef,
            count: Number(roomSplit[1]),
            addons: [{ id: addonId, count: Number(roomSplit[1]) }],
          };
          if (offerId) {
            data.offer_id = offerId;
          }
          return data;
        } else {
          const data: BookingRoom = {
            id: roomId,
            _ref: roomRef,
            count: Number(roomSplit[1]),
          };
          if (offerId) {
            data.offer_id = offerId;
          }
          return data;
        }
      });
      if (roomsData.every((room: Obj) => !isNaN(room.count))) {
        return roomsData;
      }
    }
    return null;
  };

  const getURIFromBookingDetails: (bookingDetails: Booking) => string = (
    bookingDetails: Booking
  ) => {
    const _bookingDetails: any = { ...bookingDetails };
    delete _bookingDetails.rooms;
    const roomsData = bookingDetails.rooms;
    _bookingDetails.rooms = encodeRooms(roomsData);
    return Object.keys(_bookingDetails)
      .map((key) => key + "=" + _bookingDetails[key])
      .join("&");
  };

  const decodeBookingDetailsFromURI: (uri: string) => Booking | null = (
    uri: string
  ) => {
    const REQUIRED_KEYS = ["property_code", "checkin", "checkout", "rooms"];
    const keys = uri.slice(1).split("&");
    if (keys.length >= 4 && keys.every((key) => key.includes("="))) {
      const objectFromKeys: Obj = {};
      keys.forEach((key) => {
        const keySplit = key.split("=");
        objectFromKeys[keySplit[0]] = keySplit[1];
      });
      if (REQUIRED_KEYS.every((key) => objectFromKeys[key] != null)) {
        const rooms = decodeRooms(objectFromKeys.rooms);
        if (rooms) {
          const bookingDetails: Booking = {
            coupon_code: objectFromKeys.coupon_code || "",
            property_code: objectFromKeys.property_code,
            checkin: objectFromKeys.checkin,
            checkout: objectFromKeys.checkout,
            rooms,
          };
          return bookingDetails;
        }
      }
    }
    return null;
  };

  const getRoomDataFromBooking = (bookingRooms: any[]) => {
    const rooms: any = [];
    const groupedRooms: any = {};

    bookingRooms.forEach((room: any) => {
      room._ref = `${room.id}`;
      if (room.addons?.length) {
        room.addons.forEach((a: any) => {
          const _newRoom = {
            ...room,
            _ref: `${room.id}-${a.id}`,
            price: room.price + a.price,
            offer_discount: room.offer_discount + a.offer_discount,
            discount: room.discount + a.discount,
            total_amount:
              room.total_amount * room.units + a.total_amount * a.units,
            tax_breakup: {
              cgst:
                room.tax_breakup.cgst * room.units +
                a.tax_breakup.cgst * a.units,
              cgst_percent:
                room.tax_breakup.cgst_percent + a.tax_breakup.cgst_percent,
              sgst:
                room.tax_breakup.sgst * room.units +
                a.tax_breakup.sgst * a.units,
              sgst_percent:
                room.tax_breakup.sgst_percent + a.tax_breakup.sgst_percent,
            },
            status: room.status, // Ensure status is carried over
          };
          rooms.push(_newRoom);
        });
      } else {
        rooms.push(room);
      }
    });

    rooms.forEach((room: any) => {
      const existingRoom = groupedRooms[room._ref];
      const isActive = room.status !== 3;

      if (existingRoom) {
        groupedRooms[room._ref] = {
          ...existingRoom,
          price: room.price + existingRoom.price,
          total_amount: room.total_amount + existingRoom.total_amount,
          offer_discount: room.offer_discount + existingRoom.offer_discount,
          discount: room.discount + existingRoom.discount,
          units: room.units + existingRoom.units,
          active_units: isActive
            ? (existingRoom.active_units || 0) + room.units
            : existingRoom.active_units || 0,
          tax_breakup: {
            cgst: room.tax_breakup.cgst + existingRoom.tax_breakup.cgst,
            cgst_percent: room.tax_breakup.cgst_percent, // Assuming percent remains same
            sgst: room.tax_breakup.sgst + existingRoom.tax_breakup.sgst,
            sgst_percent: room.tax_breakup.sgst_percent, // Assuming percent remains same
          },
        };
      } else {
        groupedRooms[room._ref] = {
          ...room,
          active_units: isActive ? room.units : 0,
        };
      }
    });

    return Object.values(groupedRooms);
  };


  const getOccupancyFromBooking = (booking: Booking, rooms: PropertyRoom[]) => {
    const bookedRoomIdsObject: GeneralObject = {};
    booking.rooms.forEach((r) => {
      bookedRoomIdsObject[r.id] = (bookedRoomIdsObject[r.id] || 0) + r.count;
    });
    const bookedRooms = rooms.filter(
      (r) => Object.keys(bookedRoomIdsObject).indexOf(String(r.id)) !== -1
    );
    const occupancy = bookedRooms.reduce(
      (sum, room) => (room.occupancy || 0) * bookedRoomIdsObject[room.id] + sum,
      0
    );
    return occupancy;
  };

  const roomSorter = (roomA: any, roomB: any) => {
    let priority = 0;
    if (roomA.availability != null && roomB.availability != null) {
      if (!roomA.availability.isAvailable && !roomB.availability.isAvailable) {
        priority = -1;
      } else if (
        roomA.availability.isAvailable &&
        !roomB.availability.isAvailable
      ) {
        priority = -1;
      } else if (
        !roomA.availability.isAvailable &&
        roomB.availability.isAvailable
      ) {
        priority = 1;
      } else {
        priority = roomB.priority - roomA.priority;
      }
    }
    return priority;
  };

  return {
    getAvailability,
    createNewBooking,
    decodeBookingSearchParams,
    getHostelData,
    decodeDatesFromSearchParams,
    getRoomAvailabilityAndMaxUnits,
    getRoomPrice,
    createRoomsFromAvailability,
    createOfferedRoomsFromAvailability,
    buildAddonRooms,
    sortPropertyImages,
    getURIFromBookingDetails,
    decodeBookingDetailsFromURI,
    encodeRooms,
    decodeRooms,
    getRoomTaxes,
    applyCoupon,
    getRoomDataFromBooking,
    getOccupancyFromBooking,
    roomSorter,
  };
};

export default useBookingService;
